// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '@environment';
import { CompanyService, ToastService } from '@lfx/core/services';
import { InfluenceApiResponse } from '@lfx/key-projects/key-projects.model';
import { TimeRangeValue } from '@lfx/key-projects/entities';

const DEFAULT_TIME_RANGE_TYPE: TimeRangeValue = 'past_2_years';

/**
 * API service for influence data operations
 * Single Responsibility: Handle ecosystem and technical influence HTTP operations
 */
@Injectable({
  providedIn: 'root',
})
export class InfluenceDataService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  private readonly toastService = inject(ToastService);

  /**
   * Get ecosystem influence data
   */
  getEcosystemInfluenceData(
    projectSlug: string,
    timeRangeType: TimeRangeValue = DEFAULT_TIME_RANGE_TYPE,
    metricType?: string
  ): Observable<InfluenceApiResponse> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          this.toastService.showError('No salesforce account ID found for company');
          return of(this.getEmptyInfluenceResponse(projectSlug, timeRangeType));
        }

        let params = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', projectSlug)
          .set('timeRangeType', timeRangeType);

        if (metricType) {
          params = params.set('metricType', metricType);
        }

        const url = `${environment.apiUrl}/influence/ecosystem`;
        return this.http.get<InfluenceApiResponse>(url, { params });
      }),
      catchError(() => {
        this.toastService.showError('Error fetching ecosystem influence data by slug');
        return of(this.getEmptyInfluenceResponse(projectSlug, timeRangeType));
      })
    );
  }

  /**
   * Get technical influence data
   * @param projectSlug - The project slug
   * @param timeRangeType - Time range filter: 'past_365_days' | 'past_2_years' | 'alltime'
   */
  getTechnicalInfluenceData(
    projectSlug: string,
    timeRangeType: TimeRangeValue = DEFAULT_TIME_RANGE_TYPE
  ): Observable<InfluenceApiResponse> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          this.toastService.showError('No salesforce account ID found for company');
          return of(this.getEmptyInfluenceResponse(projectSlug, timeRangeType));
        }

        const params = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', projectSlug)
          .set('timeRangeType', timeRangeType);

        const url = `${environment.apiUrl}/influence/technical`;
        return this.http.get<InfluenceApiResponse>(url, { params });
      }),
      catchError(() => {
        this.toastService.showError('Error fetching technical influence data by slug');
        return of(this.getEmptyInfluenceResponse(projectSlug, timeRangeType));
      })
    );
  }

  /**
   * Helper method to create empty influence response
   */
  private getEmptyInfluenceResponse(projectSlug: string, timeRangeType: TimeRangeValue): InfluenceApiResponse {
    return {
      accountId: '',
      projectId: '',
      projectSlug,
      timeRangeType,
      metrics: [],
    };
  }
}

